import React, { useState } from "react";
import Button from "../ui/Button.jsx";
import { useCreateEvent } from "../events/useCreateEvent.js";

function JsonEventsImporter() {
    const [jsonInput, setJsonInput] = useState("");
    const [parsedEvents, setParsedEvents] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState(new Set());
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const { isCreating, createEvent } = useCreateEvent();

    const handleParse = () => {
        try {
            const parsed = JSON.parse(jsonInput);

            if (!Array.isArray(parsed)) {
                throw new Error("JSON must be an array of events");
            }

            setParsedEvents(parsed);
            setSelectedEvents(new Set()); // Reset selections
            setError("");
        } catch (err) {
            setError("Invalid JSON: " + err.message);
            setParsedEvents([]);
            setSelectedEvents(new Set());
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        const selectedEventsArray = parsedEvents.filter((_, index) => selectedEvents.has(index));
        for (const event of selectedEventsArray) {
            createEvent(event);
        }
        setSubmitting(false);
    };

    const toggleEventSelection = (index) => {
        const newSelected = new Set(selectedEvents);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedEvents(newSelected);
    };

    const selectAll = () => {
        setSelectedEvents(new Set(parsedEvents.map((_, index) => index)));
    };

    const deselectAll = () => {
        setSelectedEvents(new Set());
    };

    return (
        <div className="p-4">
            <h2 className="text-xl font-semibold mb-2">Paste Event JSON</h2>
            <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder='Paste your JSON array here...'
                rows={10}
                className="w-full p-3 border rounded font-mono bg-pink-50"
            />

            <div className="mt-4 space-x-2">
                <Button onClick={handleParse}>
                    Parse JSON
                </Button>

                            {parsedEvents.length > 0 && (
                <div className="space-x-2">
                    <Button onClick={handleSubmit} disabled={isCreating || submitting || selectedEvents.size === 0}>
                        {submitting ? "Submitting..." : `Submit ${selectedEvents.size} Selected Events`}
                    </Button>
                    <Button onClick={selectAll} disabled={isCreating || submitting}>
                        Select All
                    </Button>
                    <Button onClick={deselectAll} disabled={isCreating || submitting}>
                        Deselect All
                    </Button>
                </div>
            )}
            </div>

            {error && (
                <p className="text-red-600 mt-2">{error}</p>
            )}

            {parsedEvents.length > 0 && (
                <div className="mt-6 space-y-4">
                    <p className="font-medium text-green-600">
                        {parsedEvents.length} events parsed. {selectedEvents.size} selected.
                    </p>
                    {parsedEvents.map((event, index) => (
                        <div 
                            key={index} 
                            className={`p-4 border rounded shadow bg-white cursor-pointer transition-colors ${
                                selectedEvents.has(index) ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                            }`}
                            onClick={() => toggleEventSelection(index)}
                        >
                            <div className="flex items-start space-x-3">
                                <input
                                    type="checkbox"
                                    checked={selectedEvents.has(index)}
                                    onChange={() => toggleEventSelection(index)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="mt-1"
                                />
                                <div className="flex-1">
                                    <p className="font-bold">{event.name}</p>
                                    <p>{event.city}, {event.country}</p>
                                    <p>Starts: {new Date(event.startDatetime).toLocaleString()}</p>
                                    {event.venue && <p className="text-sm text-gray-600">Venue: {event.venue}</p>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default JsonEventsImporter;
